import {
  EventFeedFilterGroupKeys,
  EventFeedFiltersSchemas,
  eventFeed,
} from '@api';
import { SharedFilterState } from '@terminal/hooks/use-filter-actions/useFilterActions';
import {
  useRemoteStorage,
  WHATS_NEW_INCLUDE_COMPETITORS,
  WHATS_NEW_IS_FIRST_TIME_EVER,
  WHATS_NEW_SELECTED_WATCHLISTS,
} from '@terminal/hooks/use-remote-storage';
import { apiFetch } from '@terminal/utils/api-fetch/apiFetch';
import { mapEventFeedFiltersSchemasToFilterGroups } from '@terminal/utils/event-feed-filters/mapEventFeedSchemasToScreenerFilters';
import { create } from '@terminal/utils/store/createStore';
import {
  InsightsPill,
  MarketCatalystSubTab,
} from '@terminal/views/insight-feed/components/following/following-utils';
import { InsightFeedFilterTabs } from '@terminal/views/insight-feed/components/insight-feed-type-tab-line/InsightFeedTypeTabLine';

import {
  defaultEventFeedFilterGroups,
  tabToEventFeedFilterGroup,
  tabTypeToEventFeedType,
} from './feed-filters-shared';

export { tabToEventFeedFilterGroup, tabTypeToEventFeedType };

interface FeedFiltersStore extends SharedFilterState<EventFeedFilterGroupKeys> {
  selectedWatchlistIds: Set<string>;
  isSecondRelationshipEnabled: boolean;
  selectedWatchlistSubTab: InsightFeedFilterTabs;
  selectedPill: InsightsPill;
  selectedMarketCatalystSubTab: MarketCatalystSubTab;
  isInitialized: boolean;
  isFirstTimeEver?: boolean;
  initializeFromStorage: () => void;
  initEventFeedFilters: () => Promise<void>;
  setSelectedWatchlistIds: (ids: Set<string>) => void;
  setIsSecondRelationshipEnabled: (enabled: boolean) => void;
  handleToggleSecondRelationship: () => void;
  setSelectedWatchlistSubTab: (type: InsightFeedFilterTabs) => void;
  setSelectedPill: (pill: InsightsPill) => void;
  setSelectedMarketCatalystSubTab: (tab: MarketCatalystSubTab) => void;
  setIsFirstTimeEver: (isFirstTimeEver: boolean) => void;
}

export const useFeedFilters = create<FeedFiltersStore>(set => ({
  selectedWatchlistIds: new Set<string>(),
  isSecondRelationshipEnabled: false,
  selectedWatchlistSubTab: 'all',
  selectedPill: 'all',
  selectedMarketCatalystSubTab: 'all',
  isInitialized: false,
  filterGroups: defaultEventFeedFilterGroups,
  initializeFromStorage: () => {
    const { items } = useRemoteStorage.getState();

    const storedIdList = items?.[WHATS_NEW_SELECTED_WATCHLISTS]?.map(
      row => row.id
    );

    const isIncludeCompetitors =
      typeof items?.[WHATS_NEW_INCLUDE_COMPETITORS] === 'boolean'
        ? items[WHATS_NEW_INCLUDE_COMPETITORS]
        : undefined;

    const isFirstTimeEver =
      items?.[WHATS_NEW_IS_FIRST_TIME_EVER as keyof typeof items] === undefined;

    set({
      ...(storedIdList?.length && {
        selectedWatchlistIds: new Set(storedIdList),
      }),
      ...(isIncludeCompetitors !== undefined && {
        isSecondRelationshipEnabled: isIncludeCompetitors,
      }),
      isFirstTimeEver,
      isInitialized: true,
    });
  },
  initEventFeedFilters: async () => {
    const res = await apiFetch<EventFeedFiltersSchemas>(
      eventFeed.getFilters.path,
      { method: 'get' }
    );

    if (!res.error) {
      set({
        filterGroups: mapEventFeedFiltersSchemasToFilterGroups(
          res.data,
          defaultEventFeedFilterGroups
        ),
      });
    }
  },
  setSelectedWatchlistIds: ids => {
    set({ selectedWatchlistIds: ids });

    const { storeItems } = useRemoteStorage.getState();
    storeItems({
      [WHATS_NEW_SELECTED_WATCHLISTS]: Array.from(ids).map(id => ({ id })),
    });
  },
  setIsSecondRelationshipEnabled: enabled => {
    set({ isSecondRelationshipEnabled: enabled });

    const { storeItems } = useRemoteStorage.getState();
    storeItems({ [WHATS_NEW_INCLUDE_COMPETITORS]: enabled });
  },
  handleToggleSecondRelationship: () => {
    const next = !useFeedFilters.getState().isSecondRelationshipEnabled;
    set({ isSecondRelationshipEnabled: next });

    const { storeItems } = useRemoteStorage.getState();
    storeItems({ [WHATS_NEW_INCLUDE_COMPETITORS]: next });
  },
  setSelectedWatchlistSubTab: type => {
    set({ selectedWatchlistSubTab: type });
  },
  setSelectedPill: pill => {
    set({ selectedPill: pill });
  },
  setSelectedMarketCatalystSubTab: tab => {
    set({ selectedMarketCatalystSubTab: tab });
  },
  setIsFirstTimeEver: isFirstTimeEver => {
    const { storeItems } = useRemoteStorage.getState();
    set({ isFirstTimeEver });
    storeItems({ [WHATS_NEW_IS_FIRST_TIME_EVER]: isFirstTimeEver });
  },
}));
